<template>
  <section
    class="-card-view"
    :class="{ '-expanded': isExpanded(expandContentId) }"
    @mouseenter="expandCard"
    @mouseleave="collapseCard"
  >
    <div class="card-content">
      <a :id="'custom-workout-' + customWorkout.id" :href="detailUrl">
        <div class="content-group -content-asset">
          <div class="group responsive-media">
            <card-src-set-image
              type="card-640"
              :img-prefix="imageUrlPrefix"
              :image="customWorkout.image"
              :title="customWorkout.title"
            />
          </div>
          <card-icon-bar
            :is-exclusive="true"
            :entity-type="entityType"
            :entity-id="customWorkout.id"
          />
        </div>
      </a>

      <div class="content-group -content-summary">
        <div class="group">
          <div class="summary-group">
            <a :href="detailUrl">
              <h1 class="content-title">
                {{ customWorkout.title }}
                <span v-if="customWorkout.subtitle" class="sub-title">
                  <br />{{ customWorkout.subtitle }}
                </span>
              </h1>
              <div class="primary-detail">
                {{ customWorkout.minutes }} Min &bull; {{ bodyFocus }}
              </div>
            </a>
          </div>
        </div>
      </div>

      <!-- expander -->
      <div v-if="showExpander" class="content-group -content-details">
        <div class="more-info">
          <expand-stats-toggle :expand-content-id="expandContentId" />

          <expand-stats-content :expand-content-id="expandContentId">
            <a :href="detailUrl">
              <div class="detail-group">
                <div class="secondary-details">
                  <ul class="detail-list">
                    <difficulty-rating
                      tag-is="li"
                      :difficulty="customWorkout.difficulty"
                    ></difficulty-rating>
                    <li>
                      <span class="detail-title"> Training Type </span>
                      <span class="detail-value">
                        {{ trainingTypes }}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <!-- stats -->
              <div class="stats-group">
                <div class="content-stats">
                  <!-- user -->
                  <stats-member
                    :show-user-complete-count="true"
                    :show-favorite="false"
                    :entity-id="customWorkout.id"
                    :entity-type="entityType"
                  ></stats-member>
                </div>
              </div>
            </a>
          </expand-stats-content>
        </div>
      </div>
    </div>

    <!-- action buttons -->
    <div class="card-actions">
      <div class="action-options">
        <div class="schedule-action">
          <calendar-button
            :entity-id="customWorkout.id"
            :entity-type="entityType"
            :entity-title="customWorkout.title"
          ></calendar-button>
        </div>

        <content-actions-button
          :entity-id="customWorkout.id"
          :entity-type="entityType"
          :entity-title="customWorkout.title"
          :edit-button="true"
          :tags-button="features.TAGS"
          :add-to-routine-button="features['ROUTINES']"
        />
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { useExpandersStore } from '@/stores/expanders'
import CardSrcSetImage from '@/components/images/card-src-set-image.vue'
import ExpandStatsContent from '@/components/cards/expand-stats-content.vue'
import ExpandStatsToggle from '@/components/cards/expand-stats-toggle.vue'
import CalendarButton from '@/components/buttons/calendar-button.vue'
import ContentActionsButton from '@/components/buttons/content-actions-button.vue'
import StatsMember from '@/components/cards/stats-member.vue'
import DifficultyRating from '@/components/videos/difficulty-rating.vue'
import CardIconBar from '@/components/cards/card-icon-bar.vue'
import type { CustomWorkoutCardType } from '@/types/model-types'

export default defineComponent({
  name: 'CustomWorkoutCard',
  components: {
    CardSrcSetImage,
    ExpandStatsContent,
    ExpandStatsToggle,
    CalendarButton,
    ContentActionsButton,
    StatsMember,
    DifficultyRating,
    CardIconBar,
  },
  props: {
    customWorkout: {
      type: Object as PropType<CustomWorkoutCardType>,
      required: true,
    },
    showExpander: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {}
  },
  computed: {
    ...mapState(useUserAuthStore, ['features', 'device', 'cdn']),
    ...mapState(useExpandersStore, ['isExpanded']),

    detailUrl() {
      return `/my/custom-workouts/${this.customWorkout.id}/${this.customWorkout.seo_url}`
    },

    imageUrlPrefix() {
      // get siteassets url from page load, allows CDN url to be added
      const siteAssetsPrefix = this.cdn.siteAssetsPrefix || '/siteassets'
      return `${siteAssetsPrefix}/${this.customWorkout.img_dir}`
    },

    entityType() {
      return 'custom_workout'
    },

    // could be multiple in db, but only show first match
    bodyFocus() {
      // elastic search has categories set, just return first in list
      if (this.customWorkout && Array.isArray(this.customWorkout.taxonomy)) {
        return this.customWorkout.taxonomy
          .filter((item: any) => item.category === 'bodyfocus')
          .map((item: any) => item.name)
          .join(', ')
      }

      return ''
    },

    // could be multiple in db, but only show first match
    trainingTypes() {
      // elastic search has categories set, just return first in list
      if (this.customWorkout && Array.isArray(this.customWorkout.taxonomy)) {
        return this.customWorkout.taxonomy
          .filter((item: any) => item.category === 'trainingtype')
          .map((item: any) => item.name)
          .join(', ')
      }

      return ''
    },

    expandContentId(): string {
      return `custom-workout-stats-${this.customWorkout.id}`
    },
  },
  methods: {
    ...mapActions(useExpandersStore, ['setExpanded']),

    expandCard() {
      if (this.showExpander && this.device.pointerType === 'fine') {
        this.setExpanded({
          id: this.expandContentId,
          expanded: true,
          collapseAll: true,
        })
      }
    },

    collapseCard() {
      if (this.showExpander && this.device.pointerType === 'fine') {
        this.setExpanded({
          id: this.expandContentId,
          expanded: false,
        })
      }
    },
  },
})
</script>
